
import { type CrawlResult, type EnhancedCrawlResult, type EnhancedPage, type Page } from '@/types/crawler';

// Function to check if a page is already enhanced
export const isEnhancedPage = (page: Page | EnhancedPage): page is EnhancedPage => {
  return 'score' in page || 'recommendations' in page;
};

// Function to enhance a basic page with scores and recommendations
export const enhancePage = (page: Page): EnhancedPage => {
  // Start with basic page data
  const enhancedPage: EnhancedPage = {
    ...page,
    score: 0,
    contentScore: 0,
    metadataScore: 0,
    linkScore: 0,
    recommendations: []
  };
  
  // Generate basic scores if needed (simplified version)
  if (!enhancedPage.score) {
    enhancedPage.score = calculatePageScore(page);
  }
  
  return enhancedPage;
};

export const enhanceCrawlResult = (result: CrawlResult): EnhancedCrawlResult => {
  // Start with the original result
  const enhancedResult: EnhancedCrawlResult = {
    ...result,
  };
  
  // Calculate overall score if it doesn't exist
  if (!enhancedResult.sitemapScore) {
    enhancedResult.sitemapScore = calculateSitemapScore(result);
  }
  
  // Ensure no top performing pages data is added
  const fieldsToRemove = [
    'topPages',
    'topPerformingPages',
    'performancePages',
    'aiReadiness',
    'ai_readiness',
    'top_performing_pages',
    'top_pages',
    'performance_pages'
  ];
  
  fieldsToRemove.forEach(field => {
    if (field in enhancedResult) {
      delete (enhancedResult as any)[field];
    }
  });
  
  return enhancedResult;
};

// Helper function to calculate a basic page score
const calculatePageScore = (page: Page): number => {
  // Simple calculation based on available metadata
  let score = 50; // Base score
  
  // Adjust based on content length
  if (page.content) {
    score += Math.min(10, page.content.length / 1000);
  }
  
  // Adjust based on metadata presence
  if (page.metadata?.title) {
    score += 5;
  }
  
  if (page.metadata?.description) {
    score += 5;
  }
  
  // Adjust based on heading structure
  if (page.headings && page.headings.length > 0) {
    score += Math.min(10, page.headings.length);
  }
  
  // Adjust based on images and links
  if (page.images) {
    score += Math.min(5, page.images.length / 2);
  }
  
  if (page.links) {
    score += Math.min(5, page.links.length / 5);
  }
  
  return Math.min(100, Math.max(0, score));
};

// Helper function to calculate sitemap score
const calculateSitemapScore = (result: CrawlResult): number => {
  // If we already have a sitemapScore, use it
  if (typeof result.sitemapScore === 'number') {
    return result.sitemapScore;
  }
  
  // If we have pages, calculate based on metrics
  if (result.pages && result.pages.length > 0) {
    // Get some basic metrics
    const totalPages = result.pages.length;
    const pagesWithErrors = result.pages.filter(p => {
      // Access potential error property safely
      const enhancedP = p as any;
      return enhancedP.errors && enhancedP.errors.length > 0;
    }).length;
    
    const errorRate = totalPages > 0 ? pagesWithErrors / totalPages : 0;
    
    // Base score starts at 70
    let score = 70;
    
    // Adjust based on error rate (fewer errors is better)
    score -= errorRate * 30;
    
    // Adjust based on number of pages (more pages is slightly better)
    score += Math.min(10, totalPages / 10);
    
    return Math.min(100, Math.max(0, score));
  }
  
  // Default score if nothing available
  return 60;
};
